import { ReactNode, useMemo } from 'react'
import i18next, { t } from 'i18next'
import { GETAllEntity } from 'src/types/api/modelView.api'
import { FormInputProps } from '@microservices/wiskey-react-components'
import { DoneAll } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'

import { FormInputsType } from '@pages/FormCreateOrEdit/types'

import { useObjectOptions } from '@components/DataSource/useObjectOptions'

import { checkValidJS, generateDefaultObjectByFields, getObjectValueName } from '@helpers'
import {
  BIND_TYPE,
  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME,
  ENTITY,
  FIELD_VALUE_TYPE,
  FORMATS_DATE_OPTIONS,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
  MODAL_TYPE,
  MODEL_TYPE,
  NAMES_FIELDS,
  OBJECT_FIELD_TYPE,
  PREFILL_OPTIONS,
  REGEX,
  SYSTEM_SELECT_COLUMN,
} from '@constants'
import { AutocompleteOption, GETDropDownFilter, ObjectShortDTO, SelectOption } from '@types'

import { EmbeddedObject } from '@gantt/components/GanttCreateOrEdit'

import { AddColumnDialogType } from '../AddColumnDialog'

import { ObjectValueOptionsType } from './useAddColumnDialog'

export type ColumnForm = {
  id?: number | string
  code: string
  title: string
  bindType?: BIND_TYPE
  value: string | null
  pathArray: EmbeddedObject[]
  objectValue: string | null
  valueType: FIELD_VALUE_TYPE | null
  columnToSort: AutocompleteOption | null
  order?: string | number
  useParameters: boolean
  pinnedColumn: AutocompleteOption | null
  enumDescription?: boolean
  useDisplayRule?: boolean
  disableEditable: boolean
  dropDownListCode?: AutocompleteOption | string | null
  dropDownWindowCode?: AutocompleteOption | string | null
  dropDownListFilters: GETDropDownFilter[]
  dropDownWindowFilters: GETDropDownFilter[]
  preFillSourceObject?: AutocompleteOption<string> | null
  preFillFilterObject?: AutocompleteOption<string> | null
  preFillLink?: EmbeddedObject[] | null
  preFillFilterName?: EmbeddedObject[] | null
  preFillLinkStr?: string
  preFillFilterNameStr?: string
  preFillType?: PREFILL_OPTIONS
  preFillStaticValue?: string
  actions: AutocompleteOption[]
  asCheckbox: boolean
  asDuration: boolean
  displayRule?: AutocompleteOption | null
  displayRuleId?: number | undefined
  formatDate?: AutocompleteOption | null
}

type useInputsParams = {
  watchBinding: BIND_TYPE | undefined
  watchValue: string
  watchObjectValue: string | undefined
  handleChangeValue: (value: string | number) => void
  valueOptions: ObjectValueOptionsType[]
  bindTypeOptions: SelectOption[]
  actionEventsOptions: AutocompleteOption[] | undefined
  handleChangeBinding: (value: string | number) => void
  toggleOpenScriptValueDialog: (open: boolean) => void
  valueType: FIELD_VALUE_TYPE | null
  isObjectLinkedValue: boolean
  objectValueOptions: ObjectValueOptionsType[]
  watchPathArray: EmbeddedObject[]
  isFieldRequired: boolean
  dropdownEntities: GETAllEntity
  watchAsCheckbox: boolean
  watchAsDuration: boolean
  fieldType: OBJECT_FIELD_TYPE | undefined | null
  watchUseDisplayRule?: boolean
  autocompleteOptionsDisplayRules?: AutocompleteOption | null
  isLoadingDisplayRules?: boolean
  objects?: ObjectShortDTO[]
  entityType?: ENTITY
  props: AddColumnDialogType
}

export const useInputs = ({
  watchBinding,
  watchObjectValue,
  isFieldRequired,
  watchPathArray,
  watchValue,
  handleChangeValue,
  valueOptions,
  bindTypeOptions,
  actionEventsOptions,
  handleChangeBinding,
  toggleOpenScriptValueDialog,
  valueType,
  entityType,
  dropdownEntities,
  isObjectLinkedValue,
  objectValueOptions,
  watchAsCheckbox,
  watchAsDuration,
  fieldType,
  watchUseDisplayRule,
  autocompleteOptionsDisplayRules,
  isLoadingDisplayRules,
  objects = [],
  props,
}: useInputsParams) => {
  const { currentRow, modalType, rawObjectFields, columns, type, isAllowPinningAllColumns } = props

  const srcObj = generateDefaultObjectByFields(rawObjectFields || [])

  const objectOptions = useObjectOptions({ objects })

  const preFillTypeOptions = useMemo(
    () => [
      { id: 0, option: PREFILL_OPTIONS.DYNAMIC, label: 'Dynamic' },
      { id: 1, option: PREFILL_OPTIONS.STATIC, label: 'Static' },
    ],
    []
  )

  const showEnumDescription: boolean =
    Boolean(
      columns.find(col => col.id === currentRow?.id && col.fieldType === OBJECT_FIELD_TYPE.ENUM)
    ) ||
    (fieldType === OBJECT_FIELD_TYPE.ENUM && modalType === MODAL_TYPE.CREATE)

  const isDatetimeTypeValue =
    valueType === FIELD_VALUE_TYPE.DATETIME ||
    (watchPathArray?.length &&
      watchPathArray[watchPathArray.length - 1]?.valueType === FIELD_VALUE_TYPE.DATETIME)

  const shouldDisplayAsCheckboxField =
    (valueType === FIELD_VALUE_TYPE.INTEGER || valueType === FIELD_VALUE_TYPE.BOOLEAN) &&
    fieldType !== OBJECT_FIELD_TYPE.ENUM

  let valueInput: FormInputProps
  switch (watchBinding) {
    case BIND_TYPE.JS:
    case BIND_TYPE.JSX: {
      valueInput = {
        name: 'value',
        inputType: GENERATOR_INPUT_TYPE.TEXTAREA,
        placeholder: i18next.t('columnInputs.value.fieldPlaceholder'),
        label: i18next.t('columnInputs.value.label'),
        maxLengthInput: MAX_INPUT_LENGTH,
        readOnly: true,
        rows: 10,
        rules: {
          required: true,
          maxLength: MAX_INPUT_LENGTH,
          validate: value => {
            if (checkValidJS(value, { srcObj }).error && watchBinding === BIND_TYPE.JS) {
              return `${t('error.valueJS')}`
            }
          },
        },
        additionalBtn: {
          isEnabled: true,
          text: 'edit',
          color: 'primary',
          variant: 'contained',
          onClick: () => toggleOpenScriptValueDialog(true),
        },
        onChangeSelect: handleChangeValue,
      }
      break
    }

    case BIND_TYPE.ACTION: {
      valueInput = {
        name: 'actions',
        inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
        placeholder: 'Actions',
        label: 'Actions',
        isOptionEqualToValue: (option, value) => option.label === value.label,
        autocompleteOptions: actionEventsOptions,
        multiple: true,
      }
      break
    }

    default: {
      valueInput = {
        name: 'valueRow',
        inputs: [
          {
            name: 'value',
            inputType: GENERATOR_INPUT_TYPE.SELECT,
            label: t('label.field'),
            placeholder: t('placeholder.field'),
            disabled: !watchBinding,
            selectOptions: valueOptions,
            renderOptionSelect: (option: ObjectValueOptionsType) => {
              return option.isAlreadyUsed ? (
                <>
                  <DoneAll sx={{ fontSize: '0.9rem', position: 'absolute', left: 2 }} />
                  <Typography ml={1}>
                    {getObjectValueName(option.name, option.fieldType, option.isPk)}
                  </Typography>
                </>
              ) : (
                getObjectValueName(option.name, option.fieldType, option.isPk)
              )
            },
            ...(watchValue !== t('placeholder.field') && {
              renderValueSelect: value => {
                const option = valueOptions.find(el => el.name === value)

                if (option) {
                  const nameWithPostfix = getObjectValueName(
                    option.name,
                    option.fieldType,
                    option.isPk
                  )

                  return (
                    <Box title={option.name !== nameWithPostfix ? nameWithPostfix : undefined}>
                      {value as ReactNode}
                    </Box>
                  )
                }

                return value
              },
            }),
            MenuProps: { PaperProps: { sx: { maxHeight: 300 } } },
            rules: {
              required: true,
              validate: value => value !== i18next.t('placeholder.field'),
            },
            onChangeSelect: handleChangeValue,
          },
        ],
      }
      break
    }
  }

  const columnInputs: FormInputProps[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('columnInputs.code.placeholder'),
      label: i18next.t('columnInputs.code.label'),
      disabled: modalType === MODAL_TYPE.EDIT,
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      rules: {
        required: true,
        validate: value => {
          if (
            value === CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME ||
            value === SYSTEM_SELECT_COLUMN
          ) {
            return `${value} ${i18next.t('columnInputs.errors.notAllowed')}`
          }

          if (columns.find(col => col.code === value && currentRow?.code !== value)) {
            return `${value} ${i18next.t('columnInputs.errors.alreadyUsed')}`
          }
        },
      },
    },
    {
      name: 'title',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: i18next.t('columnInputs.title.placeholder'),
      label: i18next.t('columnInputs.title.label'),
    },
    {
      name: 'bindType',
      inputType: GENERATOR_INPUT_TYPE.SELECT,
      placeholder: i18next.t('placeholder.bindType'),
      label: i18next.t('columnInputs.bindType.label'),
      selectOptions: bindTypeOptions,
      rules: { required: true },
      onChangeSelect: handleChangeBinding,
    },
    valueInput,
  ]

  const formatDateInput: FormInputsType[] = isDatetimeTypeValue
    ? [
        {
          name: 'formatDate',
          inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
          placeholder: 'Format Date',
          label: 'Format Date',
          autocompleteOptions: FORMATS_DATE_OPTIONS,
        },
      ]
    : []

  const valueTypeInputs: FormInputsType[] =
    watchBinding === BIND_TYPE.FIELD
      ? [
          {
            name: 'valueTypeRow',
            inputs: [
              {
                name: 'valueType',
                inputType: GENERATOR_INPUT_TYPE.INPUT,
                label: t('label.fieldType'),
                placeholder: t('placeholder.fieldType'),
                disabled: true,

                value: valueType,
              },
              ...(shouldDisplayAsCheckboxField
                ? [
                    {
                      name: 'asCheckbox',
                      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                      label: t('label.asCheckbox'),
                      labelPlacement: 'end',
                      disabled: !!watchAsDuration,
                    } as FormInputProps,
                  ]
                : []),
              ...(valueType === FIELD_VALUE_TYPE.INTEGER
                ? [
                    {
                      name: 'asDuration',
                      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
                      label: t('label.asDuration'),
                      labelPlacement: 'end',
                      disabled: !!watchAsCheckbox,
                    } as FormInputProps,
                  ]
                : []),
            ],
          },
        ]
      : []

  const sortInputs: FormInputsType[] = [
    {
      name: 'columnToSort',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: i18next.t('placeholder.columnToSort'),
      disabled: !watchBinding || watchBinding === BIND_TYPE.FIELD,
      label: i18next.t('columnInputs.columnToSort.label'),
      isOptionEqualToValue: (option, value) => option.label === value.label,
      autocompleteOptions: rawObjectFields?.map(obj => ({ id: obj.id, label: obj.name })) ?? [],
    },
  ]

  const dropdownEntityOptions: AutocompleteOption[] = useMemo(
    () =>
      dropdownEntities?.data.map(entity => ({
        id: entity.code,
        label: entity.title,
      })) ?? [],
    [dropdownEntities]
  )

  const dropwDownListFormInput: FormInputsType = {
    name: 'dropDownListCode',
    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
    placeholder: t('placeholder.dropDownList'),
    label: t('label.dropDownList'),
    autocompleteOptions: dropdownEntityOptions,
  }

  const dropwDownWindowFormInput: FormInputsType = {
    name: 'dropDownWindowCode',
    inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
    placeholder: t('placeholder.dropDownWindow'),
    label: t('label.dropDownWindow'),
    autocompleteOptions: dropdownEntityOptions,
  }

  const checkboxesInputs: FormInputsType[] = [
    {
      name: 'useDisplayRule',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('columnInputs.useDisplayRule.label'),
      labelPlacement: 'end',
      labelSx: { width: 160 },
      disabled:
        type === MODEL_TYPE.TIMELINE_OBJECT_X_COLUMN ||
        type === MODEL_TYPE.TIMELINE_OBJECT_Y_COLUMN,
    },
    {
      name: 'useParameters',
      inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
      label: i18next.t('columnInputs.useParameters.label'),
      labelPlacement: 'end',
    },
    ...(watchValue !== NAMES_FIELDS._ID &&
    watchBinding === BIND_TYPE.FIELD &&
    entityType !== ENTITY.DROP_DOWN_ENTITY
      ? [
          {
            name: 'disableEditable',
            inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
            disabled: isFieldRequired,
            label: i18next.t('columnInputs.editable.label'),
            labelPlacement: 'end',
          } as FormInputProps,
        ]
      : []),
    ...(showEnumDescription
      ? [
          {
            name: 'enumDescription',
            inputType: GENERATOR_INPUT_TYPE.CHECKBOX,
            label: i18next.t('columnInputs.enumDescription.label'),
            labelPlacement: 'end',
            labelSx: { width: 'auto' },
          } as FormInputProps,
        ]
      : []),
    ...(watchUseDisplayRule
      ? [
          {
            name: 'displayRule',
            inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
            loading: isLoadingDisplayRules,
            label: i18next.t('columnInputs.displayRule.label'),
            rules: { required: true },
            autocompleteOptions: autocompleteOptionsDisplayRules,
          } as FormInputProps,
        ]
      : []),
  ]

  const preFillSourceObjectInput: FormInputsType[] = [
    {
      name: 'preFillSourceObject',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: t('placeholder.preFillSourceObject'),
      label: t('label.preFillSourceObject'),
      autocompleteOptions: objectOptions,
    },
  ]

  const preFillTypeInput: FormInputsType[] = [
    {
      name: 'preFillType',
      inputType: GENERATOR_INPUT_TYPE.AUTOCOMPLETE,
      placeholder: 'Prefill Type',
      label: t('label.preFillType'),
      autocompleteOptions: preFillTypeOptions,
    },
  ]

  const preFillStaticValueInput: FormInputsType[] = [
    {
      name: 'preFillStaticValue',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: 'Value',
      label: t('label.preFillValue'),
      rules: {
        required: true,
      },
    },
  ]

  return {
    columnInputs,
    valueTypeInputs,
    sortInputs,
    checkboxesInputs,
    dropwDownListFormInput,
    dropwDownWindowFormInput,
    preFillSourceObjectInput,
    preFillTypeInput,
    preFillStaticValueInput,
    formatDateInput,
  }
}
